import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { ChevronDown, Search, Loader2, X, Check } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { formatDistrict } from '../utils/districtDisplay';

interface Legislator {
  fullName: string;
  firstName: string;
  lastName: string;
  chamber: string;
  district: string;
  partyAffiliation: string;
}

interface CongressionalDistrict {
  districtNumber: number;
  displayLabel: string;
  representative: string | null;
  partyAffiliation: string | null;
}

interface DistrictEntry {
  key: string;
  district: string;
  chamber: string;
  legislators: Legislator[];
}

interface DistrictPickerProps {
  selectedDistricts: string[];
  onChange: (districts: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Also offer US House districts.
   *
   * Off by default because the `legislators` roster this picker is built from
   * is state-only, and most callers are filtering state legislative work. Turn
   * it on where the thing being filtered can carry a congressional district —
   * outreach responses do, via the address lookup.
   */
  includeCongressional?: boolean;
  /**
   * Show only these chambers ('US House' | 'House' | 'Senate').
   *
   * Lets one card hold a picker per chamber instead of a single dropdown
   * grouping all three — which matters because selections within a chamber are
   * mutually exclusive per respondent, while selections across chambers are
   * not. Omit to show every chamber in one picker (the original behaviour).
   */
  chambers?: string[];
}

const DistrictPicker: React.FC<DistrictPickerProps> = ({
  selectedDistricts,
  onChange,
  placeholder = 'Select districts...',
  disabled = false,
  includeCongressional = false,
  chambers,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: legislatorsData, isLoading } = useQuery(
    ['legislators-for-district-picker'],
    async () => {
      const res = await api.get('/legislators?limit=500');
      return res.data.data.legislators as Legislator[];
    },
    { staleTime: 10 * 60 * 1000 }
  );

  const { data: congressionalData } = useQuery(
    ['congressional-districts-for-district-picker'],
    async () => {
      const res = await api.get('/legislators/congressional-districts');
      return res.data.data.districts as CongressionalDistrict[];
    },
    { staleTime: 10 * 60 * 1000, enabled: includeCongressional }
  );

  const districtMap = useMemo(() => {
    if (!legislatorsData) return new Map<string, DistrictEntry>();

    const map = new Map<string, DistrictEntry>();
    for (const leg of legislatorsData) {
      if (!leg.district) continue;
      const key = `${leg.chamber}-${leg.district}`;
      const existing = map.get(key);
      if (existing) {
        existing.legislators.push(leg);
      } else {
        map.set(key, {
          key,
          district: leg.district,
          chamber: leg.chamber,
          legislators: [leg],
        });
      }
    }

    /*
      Congressional districts come from a different source (federal
      officeholders, not the General Assembly roster) but render as one more
      chamber group. The key stays `<Chamber>-<number>` with no dash inside the
      chamber, because buildFilterWhere splits on the first dash to decide which
      stored district field to match.
    */
    for (const cd of congressionalData || []) {
      const districtNumber = String(cd.districtNumber);
      map.set(`USHouse-${districtNumber}`, {
        key: `USHouse-${districtNumber}`,
        district: districtNumber,
        chamber: 'US House',
        legislators: cd.representative
          ? [{
              fullName: cd.representative,
              firstName: '',
              lastName: '',
              chamber: 'US House',
              district: districtNumber,
              partyAffiliation: cd.partyAffiliation || '',
            }]
          : [],
      });
    }

    return map;
  }, [legislatorsData, congressionalData]);

  const groupedDistricts = useMemo(() => {
    const allowed = chambers && chambers.length > 0 ? new Set(chambers) : null;
    const entries = Array.from(districtMap.values()).filter(
      (entry) => !allowed || allowed.has(entry.chamber)
    );
    const chamberGroups: Record<string, DistrictEntry[]> = {};

    for (const entry of entries) {
      if (!chamberGroups[entry.chamber]) chamberGroups[entry.chamber] = [];
      chamberGroups[entry.chamber].push(entry);
    }

    for (const chamber of Object.keys(chamberGroups)) {
      chamberGroups[chamber].sort((a, b) => {
        const numA = parseInt(a.district, 10);
        const numB = parseInt(b.district, 10);
        if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
        return a.district.localeCompare(b.district);
      });
    }

    return Object.entries(chamberGroups).sort(([a], [b]) => a.localeCompare(b));
  }, [districtMap, chambers]);

  const filteredGrouped = useMemo(() => {
    if (!searchTerm.trim()) return groupedDistricts;

    const term = searchTerm.toLowerCase();
    return groupedDistricts
      .map(([chamber, entries]) => {
        const filtered = entries.filter((entry) => {
          if (entry.district.toLowerCase().includes(term)) return true;
          if (chamber.toLowerCase().includes(term)) return true;
          return entry.legislators.some(
            (leg) =>
              leg.fullName.toLowerCase().includes(term) ||
              leg.partyAffiliation?.toLowerCase().includes(term)
          );
        });
        return [chamber, filtered] as [string, DistrictEntry[]];
      })
      .filter(([, entries]) => entries.length > 0);
  }, [groupedDistricts, searchTerm]);

  const totalDistrictCount = useMemo(
    () => groupedDistricts.reduce((sum, [, entries]) => sum + entries.length, 0),
    [groupedDistricts]
  );

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Close the picker, not the dialog around it: Modal listens for Escape on
      // document too, and without this the whole Create-report wizard closes and
      // the user loses their filters.
      event.stopPropagation();
      setIsOpen(false);
    };

    /*
      Capture phase, deliberately. This picker is used inside <Modal>, whose panel
      stops mousedown propagation so a click inside it cannot reach the backdrop
      close handler. A document-level listener in the bubble phase therefore never
      fires, which left the dropdown with no way to close at all. Capture runs
      before the panel's handler, so outside-click works in a dialog and outside one.
    */
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('keydown', handleEscape, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('keydown', handleEscape, true);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (disabled) return;
    // A toggle, not an open. The trigger and its chevron look like a disclosure
    // control, and previously clicking either one while open did nothing.
    const next = !isOpen;
    setIsOpen(next);
    // Kept outside the state updater: an updater must stay pure, or StrictMode's
    // double invocation schedules the focus twice.
    if (next) setTimeout(() => inputRef.current?.focus(), 100);
  };

  const toggleDistrict = useCallback(
    (key: string) => {
      if (selectedDistricts.includes(key)) {
        onChange(selectedDistricts.filter((d) => d !== key));
      } else {
        onChange([...selectedDistricts, key]);
      }
    },
    [selectedDistricts, onChange]
  );

  const handleSelectAll = () => {
    const allKeys = Array.from(districtMap.keys());
    onChange(allKeys);
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const removeDistrict = (key: string) => {
    onChange(selectedDistricts.filter((d) => d !== key));
  };

  const formatParty = (party: string) => {
    if (!party) return '';
    if (party.toLowerCase().startsWith('democrat')) return 'D';
    if (party.toLowerCase().startsWith('republican')) return 'R';
    if (party.length > 3) return party.charAt(0).toUpperCase();
    return party;
  };

  const triggerLabel = useMemo(() => {
    if (selectedDistricts.length === 0) return placeholder;
    if (selectedDistricts.length === 1) {
      const entry = districtMap.get(selectedDistricts[0]);
      if (entry) return formatDistrict(null, String(entry.district), entry.chamber);
      return selectedDistricts[0];
    }
    return `${selectedDistricts.length} districts selected`;
  }, [selectedDistricts, districtMap, placeholder]);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger */}
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-disabled={disabled || undefined}
        onClick={handleToggle}
        onKeyDown={(event) => {
          // Keyboard parity for a div acting as a button.
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleToggle();
          }
        }}
        className={`
          flex items-center justify-between px-3 py-2 border border-border-light dark:border-border-dark 
          rounded-md cursor-pointer bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary-400 dark:hover:border-primary-500'}
          ${isOpen ? 'ring-2 ring-primary-500 border-primary-500' : ''}
        `}
      >
        <span
          className={
            selectedDistricts.length > 0
              ? 'text-text-primary dark:text-text-dark-primary'
              : 'text-text-tertiary dark:text-text-dark-tertiary'
          }
        >
          {triggerLabel}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 card p-0 overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b border-border-light dark:border-border-dark">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search districts or legislators..."
                className="input w-full pl-9"
              />
            </div>
          </div>

          {/* Select All / Clear All */}
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-border-light dark:border-border-dark text-xs">
            <button
              onClick={handleSelectAll}
              className="text-primary-500 hover:text-primary-600 dark:hover:text-primary-400 font-medium"
            >
              Select All ({totalDistrictCount})
            </button>
            <button
              onClick={handleClearAll}
              className="text-red-500 hover:text-red-600 dark:hover:text-red-400 font-medium"
            >
              Clear All
            </button>
          </div>

          {/* Grouped List */}
          <div className="max-h-72 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center p-4">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
                  Loading districts...
                </span>
              </div>
            ) : filteredGrouped.length > 0 ? (
              filteredGrouped.map(([chamber, entries]) => (
                <div key={chamber}>
                  <div className="sticky top-0 z-10 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-text-tertiary dark:text-text-dark-tertiary bg-background-tertiary dark:bg-background-dark-tertiary">
                    {chamber}
                  </div>
                  {entries.map((entry) => {
                    const isSelected = selectedDistricts.includes(entry.key);
                    const legDisplay = entry.legislators
                      .map((l) => {
                        const prefix = l.chamber === 'House' ? 'Rep.' : 'Sen.';
                        const party = formatParty(l.partyAffiliation);
                        return `${prefix} ${l.fullName}${party ? ` (${party})` : ''}`;
                      })
                      .join(', ');

                    return (
                      <div
                        key={entry.key}
                        onClick={() => toggleDistrict(entry.key)}
                        className={`
                          flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors
                          ${isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : 'hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary'}
                        `}
                      >
                        <div
                          className={`
                            flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center
                            ${
                              isSelected
                                ? 'bg-primary-500 border-primary-500'
                                : 'border-border-light dark:border-border-dark'
                            }
                          `}
                        >
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                              {formatDistrict(null, String(entry.district), entry.chamber)}
                            </span>
                            {legDisplay && (
                              <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary truncate">
                                — {legDisplay}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))
            ) : (
              <div className="px-3 py-4 text-center text-sm text-text-tertiary dark:text-text-dark-tertiary">
                {searchTerm
                  ? `No districts found matching "${searchTerm}"`
                  : 'No districts available'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Selected chips */}
      {selectedDistricts.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selectedDistricts.map((key) => {
            const entry = districtMap.get(key);
            const label = entry
              ? formatDistrict(null, String(entry.district), entry.chamber)
              : key;
            return (
              <span
                key={key}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
              >
                {label}
                <button
                  onClick={() => removeDistrict(key)}
                  className="hover:text-primary-900 dark:hover:text-primary-100"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DistrictPicker;
